/**
 * @file 画像を表示する前にデコードしておく。
 *
 * @what: decodeImage (1枚をデコードし終えるまで待つ) と predecodeImage (先読みした画像を
 *   デコード済みのまま少数だけ保持する)。
 * @why: スキャン画像は大きく、<img> の src を差し替えるとデコードが終わるまでの数フレーム、
 *   WebKit (iPad の Safari と Chrome) は何も表示しない。ページ送りのたびに紙面が背景色に
 *   なる (2026-10-10 代表報告)。差し替える前にデコードを済ませると、すぐに表示できる。
 *   同じ URL の画像は要素をまたいでデコード結果を共有するので、保持している Image が
 *   あれば、表示側の decode はすぐ終わる。
 * @failure: decode の無い環境 (jsdom) では待たずに済ませる。デコードの失敗も待たずに
 *   済ませる (表示側の <img> が改めて読み、そこで初めて失敗が見える)。
 */

/**
 * デコード済みのまま保持する枚数を、先読みする前後の3枚 (次・その次・前) に合わせる。
 * 1枚あたり数 MB のメモリを使うので、16枚あるキャッシュの全部は保持しない。
 */
const KEEP = 3;

/** 保持している Image (挿入順に古いものから捨てる)。 */
const kept = new Map<string, HTMLImageElement>();

/** decode を使えるか (jsdom には無い)。 */
export function canDecodeImage(): boolean {
  return typeof Image !== "undefined" && typeof Image.prototype.decode === "function";
}

function startDecode(url: string): { img: HTMLImageElement; done: Promise<void> } {
  const img = new Image();
  img.src = url;
  return { img, done: img.decode().catch(() => {}) };
}

/** 1枚をデコードし終えるまで待つ。失敗しても resolve する。 */
export function decodeImage(url: string): Promise<void> {
  if (!canDecodeImage()) return Promise.resolve();
  const hit = kept.get(url);
  if (hit) return hit.decode().catch(() => {});
  return startDecode(url).done;
}

/** 先読みした画像をデコードし、上限枚数まで保持する。 */
export function predecodeImage(url: string): void {
  if (!canDecodeImage()) return;
  const hit = kept.get(url);
  kept.delete(url);
  kept.set(url, hit ?? startDecode(url).img);
  for (const key of kept.keys()) {
    if (kept.size <= KEEP) break;
    kept.delete(key);
  }
}

/** 保持を全部捨てる (テストと、資料を離れたあとの解放用)。 */
export function resetDecodedImages(): void {
  kept.clear();
}
